import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Copy, Trash2, KeyRound, Plus, Power, PowerOff } from "lucide-react";
import { toast } from "sonner";

const AFFILIATIONS = [
  { value: "entreprise", label: "Entreprise" },
  { value: "club_sportif", label: "Club sportif" },
  { value: "association", label: "Association" },
  { value: "administration", label: "Administration" },
  { value: "autre", label: "Autre" },
];

function genCode(len = 8) {
  const alph = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let s = "";
  for (let i = 0; i < len; i++) s += alph[Math.floor(Math.random() * alph.length)];
  return s;
}

export default function AdminCodes() {
  const [codes, setCodes] = useState([]);
  const [form, setForm] = useState({
    code: "", affiliation_type: "entreprise", affiliation_name: "",
    max_uses: 0, expires_at: "", note: "",
  });
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/admin/access-codes").then((r) => setCodes(r.data)).catch(() => {});

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.code.trim() || !form.affiliation_name.trim()) {
      toast.error("Code et nom du rattachement requis");
      return;
    }
    setLoading(true);
    try {
      await api.post("/admin/access-codes", {
        ...form,
        code: form.code.trim().toUpperCase(),
        max_uses: Number(form.max_uses) || 0,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
      });
      toast.success("Code créé");
      setForm({ code: "", affiliation_type: "entreprise", affiliation_name: "", max_uses: 0, expires_at: "", note: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  const remove = async (id) => {
    if (!confirm("Supprimer ce code ?")) return;
    await api.delete(`/admin/access-codes/${id}`);
    toast.success("Supprimé");
    load();
  };

  const toggle = async (id, active) => {
    await api.patch(`/admin/access-codes/${id}/status`, { status: active ? "disabled" : "active" });
    load();
  };

  const copy = async (code) => {
    try { await navigator.clipboard.writeText(code); toast.success(`« ${code} » copié`); } catch {}
  };

  return (
    <div>
      <div className="flex items-center gap-3">
        <KeyRound className="text-[#E05638]" size={26}/>
        <h1 className="font-heading text-3xl font-bold" data-testid="admin-codes-title">Codes d'accès bénéficiaires</h1>
      </div>
      <p className="text-sm text-slate-500 mt-1 max-w-3xl">
        Seuls les particuliers disposant d'un code valide (fourni par leur employeur, club ou administration)
        peuvent créer un compte et débloquer les offres.
      </p>

      <form onSubmit={create} className="mt-6 bg-white border border-slate-200 rounded-2xl p-5 grid md:grid-cols-6 gap-3" data-testid="admin-code-form">
        <div className="md:col-span-2">
          <Label>Code</Label>
          <div className="flex gap-2 mt-1">
            <Input required value={form.code} placeholder="EX: MAIRIE-CHART-2026" onChange={(e)=>setForm((s)=>({...s, code: e.target.value.toUpperCase()}))} data-testid="admin-code-input"/>
            <Button type="button" variant="outline" onClick={()=>setForm((s)=>({...s, code: genCode()}))}>Générer</Button>
          </div>
        </div>
        <div>
          <Label>Type</Label>
          <Select value={form.affiliation_type} onValueChange={(v)=>setForm((s)=>({...s, affiliation_type: v}))}>
            <SelectTrigger data-testid="admin-code-type"><SelectValue/></SelectTrigger>
            <SelectContent>
              {AFFILIATIONS.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Label>Nom du rattachement</Label>
          <Input required value={form.affiliation_name} placeholder="Ex: Mairie de Chartres" onChange={(e)=>setForm((s)=>({...s, affiliation_name: e.target.value}))} data-testid="admin-code-affname"/>
        </div>
        <div>
          <Label>Max utilisations</Label>
          <Input type="number" min="0" value={form.max_uses} onChange={(e)=>setForm((s)=>({...s, max_uses: e.target.value}))} placeholder="0 = illimité"/>
        </div>
        <div className="md:col-span-2">
          <Label>Expiration</Label>
          <Input type="date" value={form.expires_at} onChange={(e)=>setForm((s)=>({...s, expires_at: e.target.value}))}/>
        </div>
        <div className="md:col-span-3">
          <Label>Note (interne)</Label>
          <Textarea rows={1} value={form.note} onChange={(e)=>setForm((s)=>({...s, note: e.target.value}))}/>
        </div>
        <div className="md:col-span-1 flex items-end">
          <Button type="submit" disabled={loading} className="btn-shine text-white w-full" data-testid="admin-code-submit">
            <Plus size={16} className="mr-1"/>{loading ? "…" : "Créer"}
          </Button>
        </div>
      </form>

      <div className="mt-6 bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="p-3 text-left">Code</th>
              <th className="p-3 text-left">Rattachement</th>
              <th className="p-3 text-left">Utilisations</th>
              <th className="p-3 text-left">Expire</th>
              <th className="p-3 text-left">Statut</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {codes.map((c) => {
              const active = c.status === "active";
              return (
                <tr key={c.id} className="border-t border-slate-100" data-testid={`admin-code-row-${c.id}`}>
                  <td className="p-3 font-mono font-bold text-slate-900">{c.code}</td>
                  <td className="p-3">
                    <div className="text-slate-900">{c.affiliation_name}</div>
                    <div className="text-xs text-slate-500">{AFFILIATIONS.find(a=>a.value===c.affiliation_type)?.label || c.affiliation_type}</div>
                  </td>
                  <td className="p-3">
                    <span className="font-mono">{c.used_count || 0}</span>
                    {c.max_uses ? <span className="text-slate-400"> / {c.max_uses}</span> : <span className="text-slate-400"> / ∞</span>}
                  </td>
                  <td className="p-3 text-slate-600">
                    {c.expires_at ? new Date(c.expires_at).toLocaleDateString("fr-FR") : "—"}
                  </td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${active?"bg-emerald-100 text-emerald-700":"bg-slate-100 text-slate-600"}`}>
                      {active ? "Actif" : "Désactivé"}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button onClick={()=>copy(c.code)} title="Copier le code"
                              className="p-2 rounded-lg text-slate-600 hover:bg-slate-100">
                        <Copy size={16}/>
                      </button>
                      <button onClick={()=>toggle(c.id, active)} title={active?"Désactiver":"Activer"}
                              className={`p-2 rounded-lg ${active?"text-amber-700 hover:bg-amber-50":"text-emerald-700 hover:bg-emerald-50"}`}>
                        {active ? <PowerOff size={16}/> : <Power size={16}/>}
                      </button>
                      <button onClick={()=>remove(c.id)} title="Supprimer"
                              data-testid={`admin-code-delete-${c.id}`}
                              className="p-2 rounded-lg text-rose-600 hover:bg-rose-50">
                        <Trash2 size={16}/>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {codes.length === 0 && (
              <tr><td colSpan="6" className="p-6 text-center text-slate-500">Aucun code d'accès pour l'instant.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
